import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isNil } from 'lodash';
import {
  FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY,
  FIRM_VALUATIONS_DEFINITION_DATA_QUERY_KEY,
} from 'common/constants/services/firms';
import type { DashboardDefinition } from 'common/types/dashboard/dashboardDefinition';
import {
  ColType,
  createEmptyDashboardDefinition,
  getDefaultColumnsForSummaryType,
  isDashboardDefinition,
  SpreadsheetDefinition,
  SummaryDefinition,
  SummaryDefinitionListResponse,
  SummaryVisibility,
} from 'common/types/summaryDefinitions';
import {
  useCreateFirmDefinition,
  useCreateFirmValuationsDefinition,
  useCreateFundDefinition,
  useDeleteFirmDefinition,
  useDeleteFirmValuationsDefinition,
  useDeleteFundDefinition,
  useGetFirmDefinitions,
  useGetFirmValuationsDefinitions,
  useGetFundDefinitions,
  useUpdateFirmDefinitionsOrder,
  useUpdateFirmValuationsDefinitionsOrder,
  useUpdateFundDefinitionsOrder,
} from 'services/hooks/summaryDefinitions';
import { SUMMARY_DEFINITIONS_QUERY_KEYS } from 'services/hooks/summaryDefinitions/types';
import SummaryDefinitionsService from 'services/summaryDefinitions';
import { Logger } from 'utilities';
import {
  ColumnWidth,
  OrderedDefinition,
  SUMMARY_TYPE_FIRM,
  SUMMARY_TYPE_FUND,
  SUMMARY_TYPE_VALUATIONS,
  SummaryDefinitionsContextValue,
  SummaryDefinitionsProviderProps,
  SummaryType,
} from './types';

const SummaryDefinitionsContext = createContext<SummaryDefinitionsContextValue | null>(null);

/**
 * Helper to merge and order definitions
 */
function computeOrderedDefinitions(
  definitions: SummaryDefinition[],
  globalDefinitions: SummaryDefinition[],
  order: number[]
): OrderedDefinition[] {
  const customById = new Map(definitions.map(d => [d.id, d]));

  const result: OrderedDefinition[] = [];

  // Add items in the specified order
  order.forEach(id => {
    const customDef = customById.get(id);
    if (customDef) {
      result.push({ ...customDef, isGlobal: false });
      customById.delete(id);
    }
  });

  // Add remaining custom definitions not in order
  customById.forEach(def => {
    result.push({ ...def, isGlobal: false });
  });

  // Add global definitions at the end
  globalDefinitions.forEach(def => {
    result.push({ ...def, isGlobal: true });
  });

  return result;
}

/**
 * Helper to get default spreadsheet definition
 */
function getDefaultSpreadsheetDefinition(summaryType: SummaryType): SpreadsheetDefinition {
  const columns = getDefaultColumnsForSummaryType(summaryType);

  return {
    columns: Object.fromEntries(columns.map((col, idx) => [idx, col])),
    sortKeys: [],
    groupBy: 'company',
    filters: [],
    filterExclusions: [],
  };
}

/**
 * Provider component for summary definitions state management
 */
export const SummaryDefinitionsProvider = ({
  children,
  summaryType,
  entityId,
  initialActiveDefinitionId = null,
  fetchEnabled = true,
}: SummaryDefinitionsProviderProps) => {
  const isFirmSummary = summaryType === SUMMARY_TYPE_FIRM;
  const isValuations = summaryType === SUMMARY_TYPE_VALUATIONS;

  // Fetch definitions based on summary type
  const firmQuery = useGetFirmDefinitions({
    firmId: entityId,
    enabled: isFirmSummary && Boolean(entityId) && fetchEnabled,
  });
  const fundQuery = useGetFundDefinitions({
    fundId: entityId,
    enabled: summaryType === SUMMARY_TYPE_FUND && Boolean(entityId) && fetchEnabled,
  });
  const valuationsQuery = useGetFirmValuationsDefinitions({
    firmId: entityId,
    enabled: isValuations && Boolean(entityId) && fetchEnabled,
  });

  let query;
  if (isFirmSummary) {
    query = firmQuery;
  } else if (isValuations) {
    query = valuationsQuery;
  } else {
    query = fundQuery;
  }

  // Mutations
  const createFirmMutation = useCreateFirmDefinition(entityId);
  const createFundMutation = useCreateFundDefinition(entityId);
  const createValuationsMutation = useCreateFirmValuationsDefinition(entityId);
  let createMutation;
  if (isFirmSummary) {
    createMutation = createFirmMutation;
  } else if (isValuations) {
    createMutation = createValuationsMutation;
  } else {
    createMutation = createFundMutation;
  }

  const deleteFirmMutation = useDeleteFirmDefinition(entityId);
  const deleteFundMutation = useDeleteFundDefinition(entityId);
  const deleteValuationsMutation = useDeleteFirmValuationsDefinition(entityId);
  let deleteMutation;
  if (isFirmSummary) {
    deleteMutation = deleteFirmMutation;
  } else if (isValuations) {
    deleteMutation = deleteValuationsMutation;
  } else {
    deleteMutation = deleteFundMutation;
  }

  const updateOrderFirmMutation = useUpdateFirmDefinitionsOrder(entityId);
  const updateOrderFundMutation = useUpdateFundDefinitionsOrder(entityId);
  const updateOrderValuationsMutation = useUpdateFirmValuationsDefinitionsOrder(entityId);
  let updateOrderMutation;
  if (isFirmSummary) {
    updateOrderMutation = updateOrderFirmMutation;
  } else if (isValuations) {
    updateOrderMutation = updateOrderValuationsMutation;
  } else {
    updateOrderMutation = updateOrderFundMutation;
  }

  // Local state
  const [activeDefinitionId, setActiveDefinitionId] = useState<number | null>(initialActiveDefinitionId);
  const [pendingChanges, setPendingChanges] = useState<Partial<SpreadsheetDefinition> | null>(null);
  const [columnWidths, setColumnWidthsState] = useState<Record<number, ColumnWidth[]>>({});

  // Stable refs so the setColumnWidth/resetColumnWidths callbacks never need to be recreated
  const activeDefinitionRef = useRef<OrderedDefinition | null>(null);
  const activeDefinitionIdRef = useRef<number | null>(null);
  // Mirror of columnWidths state — lets stable callbacks read the latest widths without re-creating
  const columnWidthsRef = useRef<Record<number, ColumnWidth[]>>({});

  // Computed state - memoize to prevent unnecessary re-renders
  const definitions = useMemo(() => query.data?.definitions ?? [], [query.data?.definitions]);
  const globalDefinitions = useMemo(() => query.data?.global_definitions ?? [], [query.data?.global_definitions]);
  const order = useMemo(() => query.data?.order ?? [], [query.data?.order]);

  const orderedDefinitions = useMemo(
    () => computeOrderedDefinitions(definitions, globalDefinitions, order),
    [definitions, globalDefinitions, order]
  );

  const activeDefinition = useMemo(() => {
    if (isNil(activeDefinitionId)) {
      return null;
    }
    return orderedDefinitions.find(d => d.id === activeDefinitionId) ?? null;
  }, [activeDefinitionId, orderedDefinitions]);

  const hasUnsavedChanges = pendingChanges !== null;

  const activeColumnWidths = useMemo(
    () => (activeDefinitionId !== null ? (columnWidths[activeDefinitionId] ?? []) : []),
    [columnWidths, activeDefinitionId]
  );

  const summaryDefinitionsService = useMemo(() => new SummaryDefinitionsService(), []);
  const queryClient = useQueryClient();

  // Keep refs current on every render so stable callbacks always see the latest values
  activeDefinitionRef.current = activeDefinition;
  activeDefinitionIdRef.current = activeDefinitionId;
  columnWidthsRef.current = columnWidths;

  // Set initial active definition to first available (only when fetching is enabled to avoid
  // fighting against callers that clear the active definition while stale cache is present)
  useEffect(() => {
    if (fetchEnabled && !activeDefinitionId && orderedDefinitions.length > 0) {
      setActiveDefinitionId(orderedDefinitions[0].id);
    }
  }, [fetchEnabled, activeDefinitionId, orderedDefinitions]);

  // Seed column widths from server data when a definition loads for the first time
  useEffect(() => {
    if (!activeDefinition || isNil(activeDefinitionId)) {
      return;
    }
    const serverWidths = activeDefinition.spreadsheet_definition?.columnWidths ?? [];
    setColumnWidthsState(prev => {
      if (prev[activeDefinitionId] !== undefined) {
        return prev;
      }
      return { ...prev, [activeDefinitionId]: serverWidths };
    });
  }, [activeDefinition, activeDefinitionId]);

  // Actions
  const createDefinition = useCallback(
    async (
      name: string,
      spreadsheetDefinition?: SpreadsheetDefinition,
      visibility: SummaryVisibility = 'firm',
      clonedFrom?: number | null
    ): Promise<SummaryDefinition> => {
      const baseDefinition = spreadsheetDefinition ?? getDefaultSpreadsheetDefinition(summaryType);
      const normalizedDefinition: SpreadsheetDefinition = {
        ...getDefaultSpreadsheetDefinition(summaryType),
        ...baseDefinition,
        columns: baseDefinition.columns ?? {},
        sortKeys: baseDefinition.sortKeys ?? [],
        groupBy: baseDefinition.groupBy ?? 'company',
        filters: baseDefinition.filters ?? [],
        filterExclusions: baseDefinition.filterExclusions ?? [],
      };

      const result = await createMutation.mutateAsync({
        name,
        spreadsheet_definition: normalizedDefinition,
        visibility,
        ...(isNil(clonedFrom) ? {} : { cloned_from: clonedFrom }),
      });
      return result;
    },
    [createMutation, summaryType]
  );

  // Dashboard views carry no spreadsheet payload, so we deliberately omit spreadsheet_definition and
  // let the model default fill it in rather than persisting a fabricated column set. The backend
  // rejects view_kind='dashboard' with an empty dashboard_definition, so always send a payload.
  const createDashboardDefinition = useCallback(
    async (
      name: string,
      dashboardDefinition?: DashboardDefinition,
      visibility: SummaryVisibility = 'firm',
      clonedFrom?: number | null
    ): Promise<SummaryDefinition> =>
      createMutation.mutateAsync({
        name,
        view_kind: 'dashboard',
        dashboard_definition: dashboardDefinition ?? createEmptyDashboardDefinition(),
        visibility,
        ...(isNil(clonedFrom) ? {} : { cloned_from: clonedFrom }),
      }),
    [createMutation]
  );

  const updateDefinition = useCallback(
    async (
      definitionId: number,
      updates: Partial<{ name: string; spreadsheet_definition: SpreadsheetDefinition; visibility: SummaryVisibility }>
    ): Promise<SummaryDefinition> => {
      if (summaryType === SUMMARY_TYPE_FIRM) {
        return summaryDefinitionsService.updateFirmDefinition(entityId, definitionId, updates);
      }

      if (summaryType === SUMMARY_TYPE_VALUATIONS) {
        return summaryDefinitionsService.updateFirmValuationsDefinition(entityId, definitionId, updates);
      }

      return summaryDefinitionsService.updateFundDefinition(entityId, definitionId, updates);
    },
    [entityId, summaryDefinitionsService, summaryType]
  );

  const updateCacheWithDefinition = useCallback(
    (updatedDef: SummaryDefinition) => {
      let queryKey;
      if (isFirmSummary) {
        queryKey = SUMMARY_DEFINITIONS_QUERY_KEYS.firm(entityId);
      } else if (isValuations) {
        queryKey = SUMMARY_DEFINITIONS_QUERY_KEYS.valuations(entityId);
      } else {
        queryKey = SUMMARY_DEFINITIONS_QUERY_KEYS.fund(entityId);
      }
      queryClient.setQueryData<SummaryDefinitionListResponse>(queryKey, prev => {
        if (!prev) {
          return prev;
        }
        return {
          ...prev,
          definitions: prev.definitions.map(d => (d.id === updatedDef.id ? updatedDef : d)),
        };
      });
    },
    [queryClient, entityId, isFirmSummary, isValuations]
  );

  const deleteDefinition = useCallback(
    async (definitionId: number): Promise<void> => {
      await deleteMutation.mutateAsync(definitionId);
      if (activeDefinitionId === definitionId) {
        const remaining = orderedDefinitions.filter(d => d.id !== definitionId);
        setActiveDefinitionId(remaining.length > 0 ? remaining[0].id : null);
      }
    },
    [deleteMutation, activeDefinitionId, orderedDefinitions]
  );

  const cloneDefinition = useCallback(
    async (sourceDefinitionId: number, newName: string): Promise<SummaryDefinition> => {
      const sourceDefinition = orderedDefinitions.find(definition => definition.id === sourceDefinitionId);

      if (!sourceDefinition) {
        throw new Error(`Unable to find summary definition with id ${sourceDefinitionId}`);
      }

      const spreadsheetDefinition =
        sourceDefinition.spreadsheet_definition ??
        sourceDefinition.spreadsheetDefinition ??
        getDefaultSpreadsheetDefinition(summaryType);

      const isGlobalDefinition = (sourceDefinition as OrderedDefinition).isGlobal;
      const clonedFrom = isGlobalDefinition ? sourceDefinition.id : (sourceDefinition.cloned_from ?? null);
      const visibility: SummaryVisibility = 'firm';

      // Clone through the matching create path, otherwise a dashboard would silently come back as
      // a spreadsheet (createDefinition only ever sends spreadsheet_definition).
      if (isDashboardDefinition(sourceDefinition)) {
        return createDashboardDefinition(
          newName,
          sourceDefinition.dashboard_definition ?? undefined,
          visibility,
          clonedFrom
        );
      }

      return createDefinition(newName, spreadsheetDefinition, visibility, clonedFrom);
    },
    [createDashboardDefinition, createDefinition, orderedDefinitions, summaryType]
  );

  const updateOrder = useCallback(
    async (newOrder: number[]): Promise<void> => {
      await updateOrderMutation.mutateAsync({ order: newOrder });
    },
    [updateOrderMutation]
  );

  const moveDefinition = useCallback(
    async (definitionId: number, direction: 'up' | 'down'): Promise<void> => {
      const currentIndex = order.indexOf(definitionId);
      if (currentIndex === -1) {
        return;
      }

      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0 || newIndex >= order.length) {
        return;
      }

      const newOrder = [...order];
      [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];

      await updateOrder(newOrder);
    },
    [order, updateOrder]
  );

  // Column management
  const addColumn = useCallback(
    (columnType: ColType, index?: number) => {
      const currentDef = activeDefinition?.spreadsheet_definition ?? activeDefinition?.spreadsheetDefinition;
      if (!currentDef) {
        return;
      }

      const currentColumns = { ...currentDef.columns };
      const columnValues = Object.values(currentColumns);

      if (columnValues.includes(columnType)) {
        Logger.warn(`Column type ${columnType} already exists`);
        return;
      }

      const existingValues = Object.entries(currentColumns)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([, colType]) => colType);

      const insertIndex = index ?? existingValues.length;

      // Insert the new column at the specified index
      const newValues = [...existingValues.slice(0, insertIndex), columnType, ...existingValues.slice(insertIndex)];

      const newColumns = Object.fromEntries(newValues.map((col, idx) => [idx, col])) as Record<number, ColType>;

      setPendingChanges(prev => ({
        ...(prev ?? currentDef),
        columns: newColumns,
      }));
    },
    [activeDefinition]
  );

  const removeColumn = useCallback(
    (columnType: ColType) => {
      const currentDef = activeDefinition?.spreadsheet_definition ?? activeDefinition?.spreadsheetDefinition;
      if (!currentDef) {
        return;
      }

      const filteredValues = Object.entries(currentDef.columns)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([, colType]) => colType)
        .filter(col => col !== columnType);

      const newColumns = Object.fromEntries(filteredValues.map((col, idx) => [idx, col])) as Record<number, ColType>;

      setPendingChanges(prev => ({
        ...(prev ?? currentDef),
        columns: newColumns,
      }));
    },
    [activeDefinition]
  );

  const reorderColumns = useCallback(
    (newColumns: Record<number, ColType>) => {
      const currentDef = activeDefinition?.spreadsheet_definition ?? activeDefinition?.spreadsheetDefinition;
      if (!currentDef) {
        return;
      }

      setPendingChanges(prev => ({
        ...(prev ?? currentDef),
        columns: newColumns,
      }));
    },
    [activeDefinition]
  );

  const setColumnWidth = useCallback(
    (column: string, width: number) => {
      const defId = activeDefinitionIdRef.current;
      const def = activeDefinitionRef.current;
      if (isNil(defId) || !def) {
        return;
      }

      const existing = columnWidthsRef.current[defId] ?? [];
      const updated = [...existing.filter(cw => cw.column !== column), { column, width }];

      setColumnWidthsState(prev => ({ ...prev, [defId]: updated }));

      const currentDef = def.spreadsheet_definition ?? def.spreadsheetDefinition;
      updateDefinition(defId, {
        spreadsheet_definition: { ...currentDef, columnWidths: updated } as SpreadsheetDefinition,
      }).then(updateCacheWithDefinition);
    },
    [updateDefinition, updateCacheWithDefinition]
  );

  const resetColumnWidths = useCallback(() => {
    const defId = activeDefinitionIdRef.current;
    const def = activeDefinitionRef.current;
    if (isNil(defId) || !def) {
      return;
    }
    setColumnWidthsState(prev => ({ ...prev, [defId]: [] }));
    const currentDef = def.spreadsheet_definition ?? def.spreadsheetDefinition;
    updateDefinition(defId, {
      spreadsheet_definition: { ...currentDef, columnWidths: [] } as SpreadsheetDefinition,
    }).then(updateCacheWithDefinition);
  }, [updateDefinition, updateCacheWithDefinition]);

  const setColumnOrder = useCallback(
    (columns: Record<number, ColType>): Promise<void> => {
      const defId = activeDefinitionIdRef.current;
      const def = activeDefinitionRef.current;
      if (isNil(defId) || !def) {
        return Promise.resolve();
      }

      const currentDef = def.spreadsheet_definition ?? def.spreadsheetDefinition;
      return updateDefinition(defId, {
        spreadsheet_definition: { ...currentDef, columns } as SpreadsheetDefinition,
      }).then(updateCacheWithDefinition);
    },
    [updateDefinition, updateCacheWithDefinition]
  );

  const setSortKeys = useCallback(
    (sortKeys: SpreadsheetDefinition['sortKeys']) => {
      const currentDef = activeDefinition?.spreadsheet_definition ?? activeDefinition?.spreadsheetDefinition;
      if (!currentDef) {
        return;
      }

      setPendingChanges(prev => ({
        ...(prev ?? currentDef),
        sortKeys,
      }));
    },
    [activeDefinition]
  );

  const setGroupBy = useCallback(
    (groupBy: SpreadsheetDefinition['groupBy']) => {
      const currentDef = activeDefinition?.spreadsheet_definition ?? activeDefinition?.spreadsheetDefinition;
      if (!currentDef) {
        return;
      }

      setPendingChanges(prev => ({
        ...(prev ?? currentDef),
        groupBy,
      }));
    },
    [activeDefinition]
  );

  const setFilters = useCallback(
    (filters: SpreadsheetDefinition['filters']) => {
      const currentDef = activeDefinition?.spreadsheet_definition ?? activeDefinition?.spreadsheetDefinition;
      if (!currentDef) {
        return;
      }

      setPendingChanges(prev => ({
        ...(prev ?? currentDef),
        filters,
      }));
    },
    [activeDefinition]
  );

  const saveChanges = useCallback(async () => {
    if (!activeDefinition || !pendingChanges) {
      return;
    }

    const currentDef = activeDefinition.spreadsheet_definition ?? activeDefinition.spreadsheetDefinition;
    const mergedDef: SpreadsheetDefinition = {
      ...currentDef,
      ...pendingChanges,
      columnWidths: activeColumnWidths,
    } as SpreadsheetDefinition;

    await updateDefinition(activeDefinition.id, {
      spreadsheet_definition: mergedDef,
    });

    setPendingChanges(null);
    const dataQueryKey = isValuations
      ? FIRM_VALUATIONS_DEFINITION_DATA_QUERY_KEY
      : FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY;
    await Promise.all([query.refetch(), queryClient.invalidateQueries({ queryKey: [dataQueryKey] })]);
  }, [activeDefinition, pendingChanges, activeColumnWidths, updateDefinition, query, isValuations, queryClient]);

  const discardChanges = useCallback(() => {
    setPendingChanges(null);
  }, []);

  const refetch = useCallback(async () => {
    await query.refetch();
  }, [query]);

  // Build memoized context value to prevent unnecessary re-renders
  const contextValue = useMemo<SummaryDefinitionsContextValue>(
    () => ({
      // State
      definitions,
      globalDefinitions,
      order,
      orderedDefinitions,
      activeDefinitionId,
      activeDefinition,
      hasUnsavedChanges,
      pendingChanges,
      columnWidths,
      activeColumnWidths,
      isLoading: query.isLoading,
      isFetching: query.isFetching,
      isFetched: query.isFetched,
      isSaving: createMutation.isPending || deleteMutation.isPending || updateOrderMutation.isPending,
      error: query.error,
      // Actions
      createDefinition,
      createDashboardDefinition,
      updateDefinition,
      deleteDefinition,
      cloneDefinition,
      updateOrder,
      moveDefinition,
      setActiveDefinition: setActiveDefinitionId,
      addColumn,
      removeColumn,
      reorderColumns,
      setColumnWidth,
      resetColumnWidths,
      setColumnOrder,
      setSortKeys,
      setGroupBy,
      setFilters,
      saveChanges,
      discardChanges,
      refetch,
    }),
    [
      definitions,
      globalDefinitions,
      order,
      orderedDefinitions,
      activeDefinitionId,
      activeDefinition,
      hasUnsavedChanges,
      pendingChanges,
      columnWidths,
      activeColumnWidths,
      query.isLoading,
      query.isFetching,
      query.isFetched,
      query.error,
      createMutation.isPending,
      deleteMutation.isPending,
      updateOrderMutation.isPending,
      createDefinition,
      createDashboardDefinition,
      updateDefinition,
      deleteDefinition,
      cloneDefinition,
      updateOrder,
      moveDefinition,
      setActiveDefinitionId,
      addColumn,
      removeColumn,
      reorderColumns,
      setColumnWidth,
      resetColumnWidths,
      setColumnOrder,
      setSortKeys,
      setGroupBy,
      setFilters,
      saveChanges,
      discardChanges,
      refetch,
    ]
  );

  return <SummaryDefinitionsContext.Provider value={contextValue}>{children}</SummaryDefinitionsContext.Provider>;
};

/**
 * Hook to access summary definitions context
 */
export function useSummaryDefinitions(): SummaryDefinitionsContextValue {
  const context = useContext(SummaryDefinitionsContext);
  if (!context) {
    throw new Error('useSummaryDefinitions must be used within a SummaryDefinitionsProvider');
  }
  return context;
}

export default SummaryDefinitionsContext;
